/** Convention: object_action, snake_case, past tense for completed actions */
export const AnalyticsEvents = {
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  PROJECT_CREATED: 'project_created',
  AI_AGENT_CREATED: 'ai_agent_created',
  MESSAGE_SENT: 'message_sent',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  PAGE_VIEWED: 'page_viewed',
  // Marketplace funnel
  SEARCH_PERFORMED: 'search_performed',
  CRAFTSMAN_PROFILE_VIEWED: 'craftsman_profile_viewed',
  SERVICE_REQUEST_CREATED: 'service_request_created',
  DEPOSIT_PAYMENT_INITIATED: 'deposit_payment_initiated',
  DEPOSIT_PAYMENT_SUCCEEDED: 'deposit_payment_succeeded',
  DEPOSIT_PAYMENT_FAILED: 'deposit_payment_failed',
  JOB_ACCEPTED: 'job_accepted',
  JOB_DECLINED: 'job_declined',
  SERVICE_STATUS_CHANGED: 'service_status_changed',
  SERVICE_HOURS_LOGGED: 'service_hours_logged',
  TAIL_PAYMENT_INITIATED: 'tail_payment_initiated',
  TAIL_PAYMENT_SUCCEEDED: 'tail_payment_succeeded',
  SERVICE_COMPLETED: 'service_completed',
  REVIEW_SUBMITTED: 'review_submitted',
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

export interface EventPropertiesMap {
  user_signed_up: {
    signup_method: 'email' | 'google' | 'line' | 'oauth' | 'invite';
    referral_source?: string;
    user_type?: string;
  };

  user_logged_in: {
    login_method: 'email' | 'google' | 'line' | 'oauth' | 'magic_link';
    is_returning_user?: boolean;
    user_type?: string;
  };

  project_created: {
    project_id: string;
    project_type?: string;
    category_id?: number;
    pricing_mode?: string;
  };

  ai_agent_created: {
    agent_id: string;
    agent_type?: string;
    model?: string;
    project_id?: string;
    category_id?: number;
  };

  message_sent: {
    conversation_id: string;
    message_type: 'user' | 'agent' | 'system' | 'text' | 'template';
    channel?: 'chat' | 'email' | 'api';
    message_length?: number;
  };

  subscription_upgraded: {
    plan_id: string;
    plan_name: string;
    billing_interval: 'monthly' | 'quarterly' | 'yearly';
    mrr_delta?: number;
    currency?: string;
  };

  subscription_cancelled: {
    plan_id: string;
    plan_name: string;
    cancel_reason?: string;
    effective_at?: string;
    cancel_immediately?: boolean;
  };

  page_viewed: {
    page_path: string;
    page_search?: string;
    page_title?: string;
  };

  search_performed: {
    query?: string;
    category_id?: number;
    results_count: number;
    view_mode?: 'map' | 'list';
    search_type?: 'all' | 'craftsman' | 'company';
  };

  craftsman_profile_viewed: {
    craftsman_id?: string;
    company_id?: string;
    source?: 'search' | 'map' | 'recommend' | 'direct';
  };

  service_request_created: {
    request_id: string;
    category_id?: number;
    pricing_mode?: string;
    is_urgent?: boolean;
    has_photos?: boolean;
  };

  deposit_payment_initiated: {
    request_id?: string;
    amount: number;
    payment_method?: string;
  };

  deposit_payment_succeeded: {
    request_id?: string;
    amount?: number;
    transaction_id?: string;
  };

  deposit_payment_failed: {
    request_id?: string;
    transaction_id?: string;
    error_code?: string;
    error_message?: string;
  };

  job_accepted: {
    request_id: string;
    craftsman_id?: string;
    company_id?: string;
    time_to_accept_minutes?: number;
  };

  job_declined: {
    request_id: string;
    craftsman_id?: string;
    decline_reason?: string;
  };

  service_status_changed: {
    request_id: string;
    from_status?: string;
    to_status: string;
  };

  service_hours_logged: {
    request_id: string;
    actual_hours: number;
    actual_price?: number;
    variance_pct?: number;
  };

  tail_payment_initiated: {
    request_id: string;
    amount: number;
    payment_method?: string;
  };

  tail_payment_succeeded: {
    request_id: string;
    amount?: number;
    transaction_id?: string;
  };

  service_completed: {
    request_id: string;
    gmv?: number;
    commission?: number;
    category_id?: number;
    pricing_mode?: string;
  };

  review_submitted: {
    request_id: string;
    rating: number;
    reviewee_type?: 'craftsman' | 'company';
  };
}
